/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

/**
 * @module bulb-block-alignment/bulbblockalignmentui
 */
import { ButtonView, Plugin } from 'ckeditor5';

import { normalizeAlignmentOptions } from './utils';
import { blockAlignment } from '../../editorcustomconfig.constant';

/**
 * The bulb block alignment UI plugin.
 *
 * @extends Plugin
 */
export default class BulbBlockAlignmentUI extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbBlockAlignmentUI' as const;
  }

  /**
   * Returns the localized option titles provided by the plugin.
   *
   * @readonly
   */
  public get localizedDefaultStylesTitles() {
    const { t } = this.editor;

    return {
      'Full Size Block': t('editor_align_full_size'),
      'Side Block': t('editor_align_side'),
      'Left Aligned Block': t('editor_align_left'),
      'Centered Block': t('editor_align_center'),
      'Right Aligned Block': t('editor_align_right'),
    };
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const configuredOptions = blockAlignment.options;

    const translatedStyles = this.translateStyles(
      normalizeAlignmentOptions(configuredOptions as any),
      this.localizedDefaultStylesTitles,
    );

    // eslint-disable-next-line no-restricted-syntax
    for (const style of translatedStyles) {
      this.createButton(style);
    }
  }

  /**
   * Creates a button for each style and
   * stores it in the editor {@link module:ui/componentfactory~ComponentFactory ComponentFactory}.
   *
   * @param style object containing button information like name, title and icon.
   */
  private createButton(style): ButtonView {
    const { editor } = this;

    const componentName = `blockAlignment:${style.name}`;

    editor.ui.componentFactory.add(componentName, (locale) => {
      const command = editor.commands.get('blockAlignment');
      const view = new ButtonView(locale);

      view.set({
        label: style.title,
        icon: style.icon,
        tooltip: true,
        isToggleable: true,
      });

      view.bind('isEnabled').to(command, 'isEnabled');
      view.bind('isOn').to(command, 'value', (value) => value === style.name);

      this.listenTo(view, 'execute', () => {
        editor.execute('blockAlignment', { value: style.name });
        editor.editing.view.focus();
      });

      return view;
    });
    return null;
  }

  /**
   * Returns the translated `title` from the passed styles array.
   *
   * @param styles list of configured styles.
   * @param titles object containing translated titles.
   */
  // TODO CG: Replace any
  private translateStyles(styles, titles) {
    // eslint-disable-next-line no-restricted-syntax
    for (const style of styles) {
      // Localize the titles of the styles, if a title corresponds with
      // a localized default provided by the plugin.
      if (titles[style.title]) {
        style.title = titles[style.title];
      }
    }
    return styles;
  }
}
